'use client';

import { useEffect, useState, type ReactNode } from 'react';

import { MarketingScrolledProvider } from '../context/marketing-scroll-context';
import { MarketingFooter } from './marketing-footer';
import { MarketingHeader } from './marketing-header';

// Runs during the initial HTML parse - before first paint - so the page already
// scrolls with the document on load, with no flash of the locked app-shell body
// (see styles/marketing.css). The effect below re-applies it on client nav.
//
// Emitted through a wrapper div's dangerouslySetInnerHTML rather than a JSX
// <script> element: the HTML parser still executes it on a cold load (it is
// plain markup then), while on client-side navigation the browser never
// executes innerHTML scripts and React never sees a script element - which
// silences React 19's dev error ("Encountered a script tag while rendering a
// React component") that a JSX <script> in this client component triggered on
// every nav into the marketing group. The effect owns the client-nav path.
const UNLOCK_SCROLL_SCRIPT =
	"document.documentElement.setAttribute('data-marketing-scroll','')";
const UNLOCK_SCROLL_HTML = { __html: `<script>${UNLOCK_SCROLL_SCRIPT}</script>` };

interface MarketingShellProps {
	/** Large footer for full public pages (landing, use cases, comparisons); small elsewhere. */
	footerVariant?: 'small' | 'large';
	children: ReactNode;
}

/**
 * MarketingShell - chrome for every public page in the (marketing) route group:
 * sticky header + content + footer, scrolling with the DOCUMENT.
 *
 * The root layout pins <body> to `fixed inset-0 overflow-hidden` (the workspace
 * shell, where scroll lives inside panels). Marketing pages want the opposite -
 * natural page scroll - so this shell flags <html data-marketing-scroll> while
 * mounted and styles/marketing.css unwinds the lock. The inline script handles
 * first paint; the effect handles client-side navigation and restores the lock
 * on the way out. The header's scroll-blur reads `scrolled` (window.scrollY)
 * from context. The outer `.dark` forces the dark brand look on a pure-black
 * (`bg-black`) canvas shared by every marketing page - landing, use cases,
 * comparisons. Sections and the footer stay transparent on top of it; only the
 * cards inside them carry their own `surface`/`background` fill.
 */
export const MarketingShell = ({ footerVariant = 'large', children }: MarketingShellProps) => {
	const [scrolled, setScrolled] = useState(false);

	useEffect(() => {
		const html = document.documentElement;
		html.setAttribute('data-marketing-scroll', '');
		return () => html.removeAttribute('data-marketing-scroll');
	}, []);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 20);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	return (
		<>
			<div hidden aria-hidden dangerouslySetInnerHTML={UNLOCK_SCROLL_HTML} />
			<div className="dark relative flex min-h-dvh flex-col bg-black text-neutral">
				<MarketingScrolledProvider value={scrolled}>
					<MarketingHeader />
					{/* overflow-x-clip contains wide hero/section art without becoming a
					    scroll container (so the document keeps scrolling and the sticky
					    header stays viewport-anchored). The header is a sibling, so its
					    popover is never clipped. */}
					<main className="flex-1 overflow-x-clip">{children}</main>
					<MarketingFooter variant={footerVariant} />
				</MarketingScrolledProvider>
			</div>
		</>
	);
};
